import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ArrowUpRight, CreditCard, Users, Calendar, DollarSign, Zap } from 'lucide-react';
import { useCurrentSubscription } from '@/hooks/useCurrentSubscription';
import { useAuth } from '@/hooks/useAuth';

const QuickStats: React.FC = () => {
  // Fetch the current subscription data
  const { data: subscription, isLoading } = useCurrentSubscription();
  const { userProfile } = useAuth();
  
  // Vérifier directement si l'utilisateur est en freemium
  const isFreemium = userProfile?.niveau_compte_id === 1;
  const hasActiveSubscription = subscription?.status === 'active' || isFreemium;
  const isPaidSubscription = hasActiveSubscription && !isFreemium;
  const isManager = userProfile?.roles.includes('manager');
  
  // Set default stats data
  const statsData = {
    subscriptionCost: isPaidSubscription ? `€${subscription?.montant_total || subscription?.niveau_compte.prix}` : '€0',
    activeUsers: isPaidSubscription && isManager ? '3' : '0',
    nextPayment: isPaidSubscription ? formatNextPaymentDate(subscription?.date_fin) : '--',
    status: hasActiveSubscription ? (isFreemium ? 'Freemium' : 'Actif') : 'Inactif',
    level: hasActiveSubscription ? (isFreemium ? 'Gratuit' : subscription?.niveau_compte.niveau) : '--',
  };

  // Format the next payment date
  function formatNextPaymentDate(dateString: string | null | undefined) {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  // Calculate days until next payment
  function getDaysUntilNextPayment(dateString: string | null | undefined) {
    if (!dateString) return 'N/A';
    const nextPayment = new Date(dateString);
    const today = new Date();
    const diffTime = nextPayment.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? `Dans ${diffDays} jours` : 'Aujourd\'hui';
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="animate-pulse">
          <CardHeader className="pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Chargement...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-6 bg-muted rounded-md"></div>
          </CardContent>
        </Card>
        {/* Repeat skeleton cards for remaining stats */}
        <Card className="animate-pulse">
          <CardHeader className="pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Chargement...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-6 bg-muted rounded-md"></div>
          </CardContent>
        </Card>
        <Card className="animate-pulse">
          <CardHeader className="pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Chargement...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-6 bg-muted rounded-md"></div>
          </CardContent>
        </Card>
        <Card className="animate-pulse">
          <CardHeader className="pb-2 space-y-0">
            <CardTitle className="text-sm font-medium">Chargement...</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-6 bg-muted rounded-md"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium">Coût de l'abonnement</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{statsData.subscriptionCost}</div>
          <p className="text-xs text-muted-foreground">{isPaidSubscription ? "Par mois" : "Forfait gratuit"}</p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium">Utilisateurs actifs</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{statsData.activeUsers}</div>
          {isPaidSubscription && isManager ? (
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500 inline-flex items-center">
                <ArrowUpRight className="h-3 w-3 mr-1" /> +1
              </span>{' '}
              depuis le mois dernier
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {isManager ? "Nécessite un abonnement actif" : "Accès limité - Compte gratuit"}
            </p>
          )}
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium">{isFreemium ? "Fonctionnalités" : "Prochain paiement"}</CardTitle>
          {isFreemium ? <Zap className="h-4 w-4 text-muted-foreground" /> : <Calendar className="h-4 w-4 text-muted-foreground" />}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{isFreemium ? "Limitées" : statsData.nextPayment}</div>
          <p className="text-xs text-muted-foreground">
            {isPaidSubscription 
              ? getDaysUntilNextPayment(subscription?.date_fin) 
              : (isFreemium 
                  ? "Passez à un plan premium" 
                  : "Aucun abonnement actif")}
          </p>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
          <CardTitle className="text-sm font-medium">Statut</CardTitle>
          <CreditCard className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{statsData.status}</div>
          <p className="text-xs text-muted-foreground">{statsData.level}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuickStats;
